import { ApolloQueryResult } from 'apollo-client';
import { difference, map } from 'lodash';
import * as React from 'react';
import { Epic } from 'redux-observable';
import { Observable } from 'rxjs';

import { Action } from '../actions';
import { acceptMassActionsBannersPrice } from '../actions/banners-edit/banners-edit-mass-actions';
import {
    closeBannersPricesPopup,
    RESET_BANNERS_PRICES,
    ResetBannersPrices,
    SAVE_BANNERS_PRICES,
    SaveBannersPrices,
    saveBannersPrices,
    SwitchBannersPricesPopupForm,
    switchBannersPricesPopupForm,
    updateBannersPricesPopupErrors,
    UpdateBannersPricesPopupErrors,
    VALIDATE_BANNERS_PRICES,
    ValidateBannersPrices
} from '../actions/banners-prices';
import {
    NoticeType,
    resetGridData,
    showNoticeMessage
} from '../actions/grid-screen';
import { ShowUnsavedBannersButton } from '../components/ShowUnsavedBannersButton/ShowUnsavedBannersButton';
import { selectPriceEditableIds } from '../selectors/bannerEdit/massActions/massActions';
import { mapBannerPriceErrors } from '../service/BannerEditErrors/BannerEditErrors';
import { getBannerPriceDataToApi } from '../service/BannerHelpers/BannerHelpers';
import { getBannersPricesSavedResultMessage } from '../service/gridItemsActionsHelpers/gridItemsActionsHelpers';
import '../service/Rx';
import { EpicDeps } from '../service/StoreFactory';
import { MassActionId } from '../typings/bannersEditState';
import {
    GdUpdateAdPriceInput,
    updateAdPrices,
    validateAdPrice
} from '../typings/graphql-grid';
import { State } from '../typings/state';

type ValidateBannersPricesEpicObservable = Observable<
    | UpdateBannersPricesPopupErrors
    | SaveBannersPrices
    | SwitchBannersPricesPopupForm
>;

export const validateBannersPricesEpic: Epic<
    Action,
    Action,
    State,
    EpicDeps
> = (action$, store$, deps) => {
    return action$.ofType(VALIDATE_BANNERS_PRICES).mergeMap(
        (
            action: ValidateBannersPrices
        ): ValidateBannersPricesEpicObservable => {
            const state = store$.value;
            const { bannerId } = action;
            const bannerPrice = state.bannersPriceEditor[bannerId].bannerPrice;

            if (!bannerPrice.price) {
                return Observable.of(switchBannersPricesPopupForm(bannerId));
            }

            return Observable.fromPromise(
                deps.bannersDataProvider.validateBannerPrice([bannerPrice])
            )
                .mergeMap(
                    (
                        result: ApolloQueryResult<validateAdPrice>
                    ): ValidateBannersPricesEpicObservable => {
                        if (result.data) {
                            if (result.data.validateAdPrice) {
                                return Observable.of(
                                    updateBannersPricesPopupErrors(
                                        mapBannerPriceErrors(
                                            result.data.validateAdPrice.errors
                                        ),
                                        bannerId
                                    )
                                );
                            } else {
                                return Observable.of(
                                    saveBannersPrices(bannerId)
                                );
                            }
                        } else {
                            return Observable.of(
                                updateBannersPricesPopupErrors(
                                    mapBannerPriceErrors([
                                        {
                                            code: 'API_ERROR',
                                            params: null,
                                            path: ''
                                        }
                                    ]),
                                    bannerId
                                )
                            );
                        }
                    }
                )
                .catch(() => {
                    return Observable.of(
                        updateBannersPricesPopupErrors(
                            mapBannerPriceErrors([
                                {
                                    code: 'API_ERROR',
                                    params: null,
                                    path: ''
                                }
                            ]),
                            bannerId
                        )
                    );
                });
        }
    );
};

export const saveBannersPricesEpic: Epic<Action, Action, State, EpicDeps> = (
    action$,
    store$,
    deps
) => {
    return action$.ofType(SAVE_BANNERS_PRICES).mergeMap(
        (action: SaveBannersPrices): Observable<Action> => {
            const state = store$.value;
            const { bannerId } = action;
            const bannerPrice = state.bannersPriceEditor[bannerId].bannerPrice;
            const selectedBanners = selectPriceEditableIds(state);
            const selectedBannersCount = selectedBanners.length;
            const price = getBannerPriceDataToApi(bannerPrice);
            const requestData = selectedBanners.map(id => {
                return {
                    id: id,
                    price: price
                };
            });

            if (bannerId === MassActionId.bannersEdit) {
                return Observable.of(
                    acceptMassActionsBannersPrice(
                        MassActionId.bannersEdit,
                        bannerId
                    )
                );
            }

            const response$ = Observable.fromPromise(
                deps.bannersMutationExecutor.updateAdPricesMass(requestData as [
                    GdUpdateAdPriceInput
                ])
            );

            return response$
                .map((result: ApolloQueryResult<updateAdPrices>) => {
                    const updatedBanners =
                        result.data.updateAdPrices.updatedAds;
                    const updatedBannersCount = updatedBanners.length;
                    const unsavedBannersCount =
                        selectedBannersCount - updatedBannersCount;
                    const resultMessage = getBannersPricesSavedResultMessage(
                        updatedBannersCount,
                        unsavedBannersCount
                    );

                    if (!updatedBannersCount) {
                        showNoticeMessage(NoticeType.error, resultMessage);
                    } else if (
                        !unsavedBannersCount ||
                        unsavedBannersCount > 100
                    ) {
                        showNoticeMessage(NoticeType.success, resultMessage);
                    } else {
                        const updatedBannersList = map(
                            updatedBanners,
                            banner => {
                                return banner ? banner.id.toString() : '';
                            }
                        );
                        const unsavedBanners = difference(
                            selectedBanners,
                            updatedBannersList
                        );

                        showNoticeMessage(NoticeType.success, resultMessage, {
                            customFields: {
                                button: React.createElement(
                                    ShowUnsavedBannersButton,
                                    {
                                        pathname: state.router.pathname,
                                        login: state.commonData.login,
                                        bannersIds: unsavedBanners
                                    }
                                )
                            },
                            timeout: 'none'
                        });
                    }
                })
                .mapTo(closeBannersPricesPopup(bannerId))
                .merge(response$.mapTo(resetGridData()))
                .catch(
                    (): Observable<UpdateBannersPricesPopupErrors> => {
                        return Observable.of(
                            updateBannersPricesPopupErrors(
                                mapBannerPriceErrors([
                                    {
                                        code: 'API_ERROR',
                                        params: null,
                                        path: ''
                                    }
                                ]),
                                bannerId
                            )
                        );
                    }
                );
        }
    );
};

export const resetBannersPricesEpic: Epic<
    Action,
    Action,
    State,
    EpicDeps
> = action$ => {
    return action$
        .ofType(RESET_BANNERS_PRICES)
        .mergeMap((action: ResetBannersPrices) => {
            return Observable.of(saveBannersPrices(action.bannerId));
        });
};
